import { useEffect, useRef, useState } from "react";
import { getLastPosition, getSocket, isSocketConnected } from "../socket";

export interface SocketFrame {
  id: number;
  event: string;
  receivedAt: number;
  payload: Record<string, unknown>;
}

const MAX_FRAMES = 50;

export const POSITION_EVENTS = new Set(["position", "last_position"]);

const frameDeviceId = (payload: Record<string, unknown>): string | null => {
  const point = payload?.point as Record<string, unknown> | undefined;
  const event = payload?.event as Record<string, unknown> | undefined;
  const candidate =
    payload?.device_id ?? payload?.deviceId ?? point?.device_id ?? event?.device_id;
  return typeof candidate === "string" ? candidate : null;
};

export const useDeviceSocketFeed = (deviceId: string) => {
  const [frames, setFrames] = useState<SocketFrame[]>([]);
  const [isConnected, setIsConnected] = useState(isSocketConnected());
  const counter = useRef(0);

  useEffect(() => {
    if (!deviceId) return;

    const socket = getSocket();

    const handleAny = (event: string, payload: Record<string, unknown>) => {
      if (frameDeviceId(payload) !== deviceId) return;
      counter.current += 1;
      const frame: SocketFrame = {
        id: counter.current,
        event,
        receivedAt: Date.now(),
        payload,
      };
      setFrames((previous) => [frame, ...previous].slice(0, MAX_FRAMES));
    };

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    socket.onAny(handleAny);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    getLastPosition(deviceId);

    return () => {
      socket.offAny(handleAny);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [deviceId]);

  return { frames, isConnected };
};

export default useDeviceSocketFeed;
